import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Brand } from "@/components/Brand";
import { api, formatErr } from "@/lib/api";

const Q = ["How useful has GrantVerify been so far?", "What's confusing, slow or wrong?", "What should we add or improve next?"];

function Rating({ v, set }) {
  return <div className="flex gap-2" data-testid="checkin-rating">{[1, 2, 3, 4, 5].map((n) => (
    <button type="button" key={n} onClick={() => set(n)} aria-label={`${n} of 5`} className="transition-transform hover:scale-110" data-testid={`checkin-rating-${n}`}><Star className={`h-8 w-8 ${n <= v ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} /></button>))}</div>;
}

export default function CohortCheckin() {
  const { token } = useParams();
  const [c, setC] = useState(null);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ rating: 0, confusing: "", wish: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => { api.get(`/public/cohort-checkin/${token}`).then((r) => setC(r.data)).catch((e) => setErr(formatErr(e))); }, [token]);
  const submit = (e) => {
    e.preventDefault();
    if (!f.rating) return toast.error("Pick a rating from 1 to 5");
    setBusy(true);
    api.post(`/public/cohort-checkin/${token}`, f).then(() => setDone(true)).catch((er) => toast.error(formatErr(er))).finally(() => setBusy(false));
  };
  const body = err ? <p className="text-slate-600" data-testid="checkin-error">{err}</p>
    : !c ? <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
    : done || c.answered ? <div data-testid="checkin-done"><Check className="h-10 w-10 text-emerald-600" /><h1 className="font-display text-3xl font-extrabold mt-4">Thank you{c.name ? `, ${c.name}` : ""}.</h1><p className="text-slate-600 mt-2">Your answers went straight to the team. We'll check in again in two weeks.</p><Link to="/dashboard" className="mt-6 inline-block font-semibold text-emerald-700 hover:underline" data-testid="checkin-dashboard-link">Open GrantVerify</Link></div>
    : (
      <form onSubmit={submit} className="space-y-8" data-testid="checkin-form">
        <div><div className="eyebrow mb-3">Alpha Founder Cohort check-in</div><h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Hi {c.name}, three quick questions about {c.business}.</h1></div>
        <div><h2 className="font-semibold text-slate-900 mb-3">1. {Q[0]}</h2><Rating v={f.rating} set={(n) => setF({ ...f, rating: n })} /></div>
        <div><h2 id="checkin-q2" className="font-semibold text-slate-900 mb-3">2. {Q[1]}</h2><Textarea aria-labelledby="checkin-q2" rows={3} maxLength={1500} value={f.confusing} onChange={(e) => setF({ ...f, confusing: e.target.value })} data-testid="checkin-confusing" /></div>
        <div><h2 id="checkin-q3" className="font-semibold text-slate-900 mb-3">3. {Q[2]}</h2><Textarea aria-labelledby="checkin-q3" rows={3} maxLength={1500} value={f.wish} onChange={(e) => setF({ ...f, wish: e.target.value })} data-testid="checkin-wish" /></div>
        <Button type="submit" disabled={busy} className="h-11 px-6 rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="checkin-submit">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Send answers</Button>
      </form>
    );
  return (
    <div className="min-h-screen bg-slate-50" data-testid="cohort-checkin-page">
      <header className="bg-white border-b border-slate-200"><div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center"><Brand /></div></header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-16">{body}</main>
    </div>
  );
}
